import { Video } from "../models/swaggerDataTypes";
import { action, observable, makeObservable } from "mobx";
import { api } from "../lib/api";
import { IApiVideoQueueResponse } from "../models/apiResponses";
import {
  IAddToVideoQueueRequest,
  IUpdateVideoQueueRequest
} from "../models/apiRequests";
import { arrayMove } from "@dnd-kit/sortable";
import { SharedStateStore } from "./SharedStateStore";

export interface IQueueVideo {
  order: string;
  id: number;
  video: Video;
}

class QueueVideo implements IQueueVideo {
  order;
  id = 0;
  video;

  constructor() {
    makeObservable(this, {
      order: observable,
      id: observable,
      video: observable
    });
  }
}

export class VideoQueueStore extends SharedStateStore {
  queue: QueueVideo[] = [];
  loadingInProgress = false;
  loaded = false;

  constructor() {
    super()
    
    makeObservable(this, {
      queue: observable,
      loadQueue: action,
      updateOrder: action,
      addToQueue: action,
      removeFromQueue: action,
      updateVideoPosition: action
    });
  }

  async loadQueue() {
    if (this.loadingInProgress || this.loaded) {
      return;
    }

    await this.refreshQueue();
  }

  private async refreshQueue(): Promise<void> {
    this.setState({
      loadingInProgress: true,
      loaded: false
    })
    try {
      let newQueue = (await api.find<IApiVideoQueueResponse>("video-queue"))
        .map(entry => {
          const record = new QueueVideo();

          record.id = entry.id;
          record.video = entry.video;
          record.order = entry.order;

          return record;
        })
        .sort((a, b) => parseInt(a.order || "") - parseInt(b.order || ""));
      this.setState({ queue: newQueue })
    } catch (err) {
      throw err;
    } finally {
      this.setState({
        loadingInProgress: false,
        loaded: true
      })
    }
  }

  async updateOrder(from: number, to: number) {
    let newQueueOrder = arrayMove(this.queue, from, to);

    this.setState({ queue: newQueueOrder})

    const fromVideo = this.queue.find(
      queueVideo => parseInt(queueVideo.order || "") === from + 1
    );

    if (fromVideo) {
      this.updateVideoPosition(fromVideo, to + 1);
    }
  }

  async addToQueue(videoId?: number): Promise<void> {
    await api.post<void, IAddToVideoQueueRequest>("video-queue", {
      video: videoId || 0
    });

    await this.refreshQueue();
  }

  async removeFromQueue(videoId?: number): Promise<void> {
    if (!videoId) {
      return;
    }

    const queueVideo = this.queue.find(
      obj => obj.video && obj.video.id === videoId
    );
    if (!queueVideo) {
      return;
    }

    await api.delete(`video-queue/${queueVideo.id}`);
    await this.refreshQueue();
  }

  async updateVideoPosition(video: QueueVideo, newPosition: number) {
    await api.update<void, IUpdateVideoQueueRequest>(
      `video-queue/${video.id}`,
      {
        order: newPosition
      }
    );

    await this.refreshQueue();
  }

  inQueue(id?: number): boolean {
    if (!id) {
      return false;
    }

    return (
      this.queue.find(
        queueVideo => queueVideo.video && queueVideo.video.id === id
      ) !== undefined
    );
  }
}
